import React, { useCallback, useState, useContext } from "react";
import { ButtonDropdown, DropdownItem, DropdownMenu, DropdownToggle } from "reactstrap"
import CollapsablePanel from "../../common/collapsable-panel/collapsable-panel";
import { IOrg } from "../../../types/org";
import { AddIcon, CancelIcon, CheckIcon, DeleteIcon, MoveIcon } from "../../common/icons";
import { IPosition } from "../../../types/position";
import { useMutation, useQuery } from "@apollo/client";
import useCheckGroup from "../../../hook/useCheckGroup";
import { EXPIRE_POSITION_ROLE, GET_ORG_POSITIONS, GET_ORG_ROLES, MOVE_POSITION_ROLE } from "../../../graphql/position-gql";
import { notifySuccess } from "../../../services/notification";
import confirm from "../../common/confirm";
import { messages } from "../../../i18n/message";
import { LOCALES } from "../../../i18n/locales";
import SpinnerOverlay from "../../common/spinner/spinner";
import { CategoryType } from "../../../types/constants";
import { IIndividual } from "../../../types/individual";
import PositionRoleCreateModal from "../../org/position-role-create/position-role-create";
import MovePositionDialog from "../../org/positions-roles/move-position-role";
import PositionsIndividuals from "../../org/positions-roles/positions-individuals/positions-individuals";
import RolesIndividuals from "../../org/positions-roles/roles-individuals/roles-individuals";
import { OrgReducerContext } from "../../org/org-context";
import useAuth from "../../../hook/useAuth";
import { IS_SCOPE } from "../../../graphql/org-gql";
import Button from "../../common/button";

type SearchPositionPositionsRolesProps = {
    org: IOrg;
    //onPositionSelect:(position:IPosition|undefined)=>void;
    //onIndividualSelect:(individual:IIndividual,positionRole:IPosition)=>void;
  };

const SearchPositionPositionsRoles :React.FC<SearchPositionPositionsRolesProps> = ({ org })=>{
    const [dropdownOpen, setDropdownOpen] = useState<boolean>(false)
    const [isRole, setIsRole] = useState<boolean>(false)
    const {checkGroup: positionCheckGroup} = useCheckGroup<number>()
    const {checkGroup: roleCheckGroup} = useCheckGroup<number>()
    const [createPositionOpen, setCreatePositionOpen] = useState<boolean>(false)
    const [movePositionOpen, setMovePositionOpen] = useState<boolean>(false)
    const { data:scopeData}  = useQuery(IS_SCOPE,{variables:{orgId:org.id,isLowerLevel:false}});
    const [expirePositonRole,{loading:expireLoading}] = useMutation(EXPIRE_POSITION_ROLE)
    const [movePositionRole,{loading:moveLoading}] = useMutation(MOVE_POSITION_ROLE)
    const toggleCreatePosition = useCallback(() => {setCreatePositionOpen(!createPositionOpen)}, [createPositionOpen])
    const toggleMovePosition = useCallback(() => {setMovePositionOpen(!movePositionOpen)}, [movePositionOpen])
    const toggleDropdownOpen = useCallback(() => {setDropdownOpen(!dropdownOpen)}, [dropdownOpen])
    const move = org.details?.category?.type === CategoryType.ORGANIZATION
    const [state, dispatch] = useContext(OrgReducerContext);
    const { isFrenchTranslator } = useAuth()
    const onSelectPositionHandle=(position:IPosition)=>{
        dispatch({
            type:'set-position-search-position',
            payload:position
        })
        dispatch({
            type:'set-position-search-individual'
        })
        if (state?.individualSearchIndividual) {
            // remove individual details if a position was clicked
            dispatch({ type:'set-individual-search-individual' });
        }
        dispatch({type: 'set-last-selected-item', payload: position});
    }

    const onSelectIndividualHandle=(individual:IIndividual, positionRole:IPosition)=>{
        dispatch({
            type:'set-position-search-individual',
            payload:individual
        })
        dispatch({
            type:'set-position-search-position-role',
            payload:positionRole
        })
        dispatch({
            type:'set-position-search-position'
        })
        if (state?.individualSearchIndividual) {
            // remove individual details if a position was clicked
            dispatch({ type:'set-individual-search-individual' });
        }
        dispatch({type: 'set-last-selected-item', payload: lastSelectedIndividual(individual, positionRole)});
    }

    const onSelectRoleHandle=(role:IPosition)=>{
        dispatch({
            type:'set-position-search-position',
            payload:role
        })
        dispatch({
            type:'set-position-search-individual'
        })
        if(state?.individualSearchIndividual) {
            // remove individual details if a position was clicked
            dispatch({ type:'set-individual-search-individual' });
        }
        dispatch({type: 'set-last-selected-item', payload: role});
    }

    const onCreatePositionHandle = ()=>{
        setIsRole(false)
        toggleCreatePosition();
    }
    const onCreateRole = ()=>{
        setIsRole(true)
        toggleCreatePosition();
    }
    const expireRefetchQueries = () => {
        return [{query: GET_ORG_ROLES, variables: {  orgId: org.id||0 } },{query: GET_ORG_POSITIONS, variables: {  orgId: org.id||0 } }]
    }

    const lastSelectedIndividual=(individual:IIndividual, positionRole:IPosition)=>{
        return `select-individual-position-role-${individual.individualId}-${positionRole.positionId}`;
    }

    const onExpirePositionOrRoleHandle = () => {
        expirePositionOrRoleConfirm().then(() => {
            expirePositionOrRoleExecution()
        })
    }

    const expirePositionOrRoleConfirm = () => {
        return confirm({
            title: 'Expire selected Position(s) or Role(s)',
            content: messages[LOCALES.ENGLISH].INFO['4.1'],
            okText: <><CheckIcon/> Expire</>,
            okBtnColor: 'danger',
            titleCenter: true,
            bodyCenter: true
        })
    }

    const expirePositionOrRoleExecution=()=>{
        const positionIds = positionCheckGroup.getCheckedValues().concat(roleCheckGroup.getCheckedValues())
        expirePositonRole({
            variables:{orgId:org?.id||0,positionIds:positionIds},
            onCompleted:(data)=>{
                if (data?.expirePositionOrRole && !data.expirePositionOrRole.success) {
                    expireValidationConfirm(data.expirePositionOrRole.message)
                }else{
                    dispatch({
                        type:'set-position-search-position'
                    })
                    notifySuccess();
                }
            },
            awaitRefetchQueries:true,
            refetchQueries:expireRefetchQueries()
        })
    }

    const expireValidationConfirm = (message:string) => {
        return confirm({
            title: 'Warning!',
            content: message,
            okText: <><CancelIcon/> Close</>,
            okBtnColor: 'primary',
            titleCenter: true,
            bodyCenter: true,
            showCancel:false
        })
    }


    const onOpenMoveDialogHandle = () => {
        toggleMovePosition()
    }

    const onMoveHandle = async (destOrg: IOrg|undefined) => {
        if(!destOrg) return;
        try {
            const positionIds = positionCheckGroup.getCheckedValues().concat(roleCheckGroup.getCheckedValues());
            const validationResult = await movePositionRoleValidation(destOrg,positionIds)
            if(validationResult.data?.movePostionOrRole && !validationResult.data.movePostionOrRole.success){
                movePositionRoleValidationConfirm(validationResult.data.movePostionOrRole.message)
            }else{
                const automationResult = await movePositionRoleAutomation(destOrg,positionIds)
                if(automationResult.data?.movePostionOrRole?.message){
                    movePositionRoleAutomationConfirm(automationResult.data.movePostionOrRole.message).then(()=>{
                        movePositionRoleExecution(destOrg,positionIds)
                    })
                }else{
                    movePositionRoleExecution(destOrg,positionIds)
                }
            }
        } catch (error) {
            console.error("Error during movePositionRole:", error);
            // Handle error appropriately
        }
    };

    const movePositionRoleValidation = async (destOrg: IOrg,positionIds:number[]) => {
        return await movePositionRole({
            variables: {
                "action": "MOVE_VALIDATION",
                "sourceOrgId": org.id,
                "destOrgId": destOrg.id,
                "positionIds": positionIds,
            },
        });
    }

    const movePositionRoleValidationConfirm = (message:string) => {
        return confirm({
            title: 'Warning!',
            content: message,
            okText: <><CancelIcon/> Close</>,
            okBtnColor: 'primary',
            titleCenter: true,
            bodyCenter: true,
            showCancel:false
        })
    }

    const movePositionRoleAutomation = async (destOrg: IOrg,positionIds:number[]) => {
        return await movePositionRole({
            variables: {
                "action": "MOVE_AUTOMATION",
                "sourceOrgId": org.id,
                "destOrgId": destOrg.id,
                "positionIds": positionIds,
            },
        });
    }

    const movePositionRoleAutomationConfirm = (message:string) => {
        return confirm({
            title: 'Warning!',
            content: message,
            okText: <><CheckIcon/> OK</>,
            okBtnColor: 'primary',
            titleCenter: true,
            bodyCenter: true,
        })
    }

    const movePositionRoleExecution = (destOrg: IOrg,positionIds:number[]) => {
        movePositionRole({
            variables: {
                "action": "MOVE",
                "sourceOrgId": org.id,
                "destOrgId": destOrg.id,
                "positionIds": positionIds,
            },
            refetchQueries:moveRefetchQueries(destOrg),
            awaitRefetchQueries:true,
            onCompleted:(data)=>{
                data&&notifySuccess()
                dispatch({
                    type:'set-position-search-position'
                })
            }
        });
    }

    const moveRefetchQueries = (destOrg:IOrg) => {
        let queries:any = [{query: GET_ORG_ROLES, variables: {  orgId: org.id } },{query: GET_ORG_POSITIONS, variables: {  orgId: org.id } },
                        {query: GET_ORG_ROLES, variables: {  orgId: destOrg.id } },{query: GET_ORG_POSITIONS, variables: {  orgId: destOrg.id } }];
        if(org.sharedToOrgIds){
            org.sharedToOrgIds.split(',').forEach(orgId=>{
                queries = [...queries,{query: GET_ORG_ROLES, variables: {  orgId} },{query: GET_ORG_POSITIONS, variables: {  orgId} }]
            })
        }
        if(destOrg.sharedToOrgIds){
            destOrg.sharedToOrgIds.split(',').forEach(orgId=>{
                queries = [...queries,{query: GET_ORG_ROLES, variables: {  orgId} },{query: GET_ORG_POSITIONS, variables: {  orgId} }]
            })
        }

        return queries;
    }

    return(
        <React.Fragment>
            {(expireLoading||moveLoading) && <SpinnerOverlay />}
            {<PositionRoleCreateModal isOpen={createPositionOpen} toggle={toggleCreatePosition} isRole={isRole} org={org}></PositionRoleCreateModal>}
            {<MovePositionDialog isOpen={movePositionOpen} toggle={toggleMovePosition} onMoveHandle={onMoveHandle}></MovePositionDialog>}
            {!isFrenchTranslator && <div className="py-3">
                <ButtonDropdown isOpen={dropdownOpen} toggle={toggleDropdownOpen} style={{ marginRight: '15px' }}>
                    <DropdownToggle caret color="primary">
                        <AddIcon/>
                        &nbsp;<span role="region" aria-label="Add">Add</span>
                    </DropdownToggle>
                    <DropdownMenu style={{ overflowY: 'auto' }}>
                        <DropdownItem disabled={!scopeData?.isScope}><span role="region" aria-label="Position" onClick={onCreatePositionHandle}>Position</span></DropdownItem>
                        <DropdownItem disabled={!scopeData?.isScope}><span role="region" aria-label="Role" onClick={onCreateRole}>Role</span></DropdownItem>
                    </DropdownMenu>
                </ButtonDropdown>
                <Button color="danger" style={{ marginRight: '15px' }}
                    disabled={positionCheckGroup.isNoneChecked() && roleCheckGroup.isNoneChecked()}
                    onClick={onExpirePositionOrRoleHandle}>
                    <DeleteIcon />
                    &nbsp;<span role="region" aria-label="Expire">Expire</span>
                </Button>
                {move && <Button color="primary" style={{ marginRight: '15px' }}
                    disabled={positionCheckGroup.isNoneChecked() && roleCheckGroup.isNoneChecked()}
                    onClick={onOpenMoveDialogHandle}>
                    <MoveIcon />
                    &nbsp;<span role="region" aria-label="Move">Move</span>
                </Button>
                }
            </div>}

            <CollapsablePanel headTitle="Positions & Individuals" initCollapse={true}>
                <PositionsIndividuals  org={org} positionSelected={onSelectPositionHandle} individualSelected = {onSelectIndividualHandle} checkGroup={positionCheckGroup} lastSelectedIndividual={lastSelectedIndividual}/>
            </CollapsablePanel>

            <CollapsablePanel headTitle="Roles & Individuals" css_classes="mt-3" initCollapse={true}>
                <RolesIndividuals  org={org} onRoleSelect={onSelectRoleHandle} individualSelected = {onSelectIndividualHandle} checkGroup={roleCheckGroup} lastSelectedIndividual={lastSelectedIndividual}/>
            </CollapsablePanel>
        </React.Fragment>
    )
}
export default SearchPositionPositionsRoles;
